/**
 * Enhanced System Configuration Panel Component for Teacher Dashboard
 * Comprehensive system settings, diagnostics, and VoiceLive connection configuration
 */

import React, { useState, useEffect } from 'react';
import VersionDisplay from './VersionDisplay';
import BackendLogsViewer from './BackendLogsViewer';
import DatabaseStatusViewer from './DatabaseStatusViewer';
import PersonaPromptsTab from './dashboard/PersonaPromptsTab';
import { getApiUrl } from '../utils/apiUtils';
import { RefreshIcon, InfoIcon, CloseIcon, AlertIcon, CheckCircleIcon } from './SvgConsolidated/SvgIcons';

interface VoiceLiveConfig {
  [key: string]: { value: any; description: string; data_type: string };
}

interface ConnectionStatus {
  state: string;
  conversation_state?: string;
  is_connected: boolean;
  last_connected?: string;
}

interface SystemInfo {
  userAgent: string;
  language: string;
  platform: string;
  timestamp: string;
  timezone: string;
  screen: {
    width: number;
    height: number;
  };
}

interface LogEntry {
  timestamp: string;
  level: string;
  category: string;
  event: string;
  data?: any;
}

/* interface SystemPrompt {
  age_range: string;
  prompt_text: string;
  word_count: number;
  char_count: number;
  is_custom: boolean;
} */

interface SystemConfigPanelProps {
  onClose: () => void;
}

export default function SystemConfigPanel({ onClose }: SystemConfigPanelProps) {
  // Main state management
  const [activeTab, setActiveTab] = useState<'voicelive' | 'voicelive_session' | 'prompts' | 'system' | 'logs' | 'database' | 'help'>('voicelive');
  const [voiceLiveConfig, setVoiceLiveConfig] = useState<VoiceLiveConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Diagnostic data
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [showVersionDetails, setShowVersionDetails] = useState(false);

  // Form state for VoiceLive settings
  const [formData, setFormData] = useState<Record<string, any>>({
    voicelive_connection_initial_delay: 1.0,
    voicelive_connection_max_delay: 60.0,
    voicelive_connection_backoff_multiplier: 2.0,
    voicelive_connection_jitter_enabled: true,
    voicelive_connection_max_retries: 10,
    voicelive_connection_health_check_interval: 30,
    voicelive_transport_error_patterns: 'closing transport,connection closed,transport closed'
  });

  useEffect(() => {
    loadAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    await Promise.all([
      loadVoiceLiveConfig(),
      loadConnectionStatus(),
      loadSystemInfo(),
      loadRecentLogs()
    ]);
  };

  const loadVoiceLiveConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/v1/dashboard/config/voicelive'));
      
      if (response.ok) {
        const data = await response.json();
        setVoiceLiveConfig(data.voicelive_connection_config);
        
        // Update form data with loaded values
        const newFormData = { ...formData };
        Object.entries(data.voicelive_connection_config).forEach(([key, config]: [string, any]) => {
          if (key in newFormData) {
            newFormData[key] = config.value;
          }
        });
        setFormData(newFormData);
      } else {
        setErrors(['Failed to load VoiceLive configuration']);
      }
    } catch (error) {
      console.error('Error loading VoiceLive config:', error);
      setErrors(['Error loading configuration']);
    } finally {
      setLoading(false);
    }
  };

  const loadConnectionStatus = async () => {
    try {
      const response = await fetch(getApiUrl('/voicelive/status'));
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data);
      }
    } catch (error) {
      console.error('Error loading connection status:', error);
    }
  };

  const loadSystemInfo = async () => {
    const info: SystemInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: {
        width: window.screen.width,
        height: window.screen.height
      }
    };
    setSystemInfo(info);
  };

  const loadRecentLogs = async () => {
    try {
      // Use /v1/logs instead of /api/v1/logs since getApiUrl already handles the base URL
      const response = await fetch(getApiUrl('/v1/logs?limit=20'));
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      } else {
        console.error('Error loading logs - HTTP', response.status);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      // Set some mock logs for demonstration
      setLogs([
        {
          timestamp: new Date().toISOString(),
          level: 'INFO',
          category: 'system',
          event: 'Configuration panel opened',
          data: { user: 'teacher' }
        }
      ]);
    }
  };

  // const handleExportLogs = () => {
  //   const logsJson = JSON.stringify(logs, null, 2);
  //   const blob = new Blob([logsJson], { type: 'application/json' });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement('a');
  //   a.href = url;
  //   a.download = `system-logs-${new Date().toISOString().split('T')[0]}.json`;
  //   document.body.appendChild(a);
  //   a.click();
  //   document.body.removeChild(a);
  //   URL.revokeObjectURL(url);
  // };

  // const handleClearLogs = async () => {
  //   try {
  //     // Use /v1/logs instead of /api/v1/logs since getApiUrl already handles the base URL
  //     const response = await fetch(getApiUrl('/v1/logs'), { method: 'DELETE' });
  //     if (response.ok) {
  //       setLogs([]);
  //     }
  //   } catch (error) {
  //     console.error('Error clearing logs:', error);
  //   }
  // };

  const handleSeedMockData = async () => {
    try {
      setLoading(true);
      setErrors([]);
      setSuccessMessage('');

      const response = await fetch(getApiUrl('/v1/dashboard/seed-mock-data'), { 
        method: 'POST' 
      });
      
      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`Mock data seeded successfully! Added ${result.data_seeded.stories} stories and ${result.data_seeded.sessions} sessions.`);
      } else {
        const error = await response.text();
        setErrors([`Failed to seed mock data: ${error}`]);
      }
    } catch (error) {
      console.error('Error seeding mock data:', error);
      setErrors(['Error seeding mock data. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear ALL data? This cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setErrors([]);
      setSuccessMessage('');

      const response = await fetch(getApiUrl('/v1/dashboard/clear-data'), { 
        method: 'DELETE' 
      });
      
      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`All data cleared successfully! Cleared ${result.cleared_tables.length} tables.`);
      } else {
        const error = await response.text();
        setErrors([`Failed to clear data: ${error}`]);
      }
    } catch (error) {
      console.error('Error clearing data:', error);
      setErrors(['Error clearing data. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key: string, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Clear messages when user makes changes
    setErrors([]);
    setSuccessMessage('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrors([]);
      setSuccessMessage('');

      // Prepare updates array
      const updates = Object.entries(formData).map(([key, value]) => ({
        key,
        value: String(value),
        category: 'voicelive_connection'
      }));

      const response = await fetch(getApiUrl('/v1/dashboard/config/voicelive'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          setSuccessMessage(`Successfully updated ${result.updated.length} VoiceLive settings`);
          
          // Refresh configuration on server
          await fetch(getApiUrl('/v1/dashboard/config/refresh'), { method: 'POST' });
          
          // Reload the configuration to show updated values
          await loadVoiceLiveConfig();
        } else {
          setErrors(result.errors || ['Update failed']);
        }
      } else {
        setErrors(['Failed to update configuration']);
      }
    } catch (error) {
      console.error('Error saving VoiceLive config:', error);
      setErrors(['Error saving configuration']);
    } finally {
      setSaving(false);
    }
  };

  // Tab render functions
  const renderPromptsTab = () => (
    <PersonaPromptsTab
      onError={(message) => setErrors([message])}
      onSuccess={(message) => setSuccessMessage(message)}
    />
  );

  const renderVoiceLiveTab = () => (
    <div className="space-y-6">
      {/* Connection Status Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Current Connection Status</h3>
          <button 
            onClick={loadConnectionStatus}
            className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
          >
            <RefreshIcon className="w-4 h-4 mr-1" />
            Refresh Status
          </button>
        </div>
        {connectionStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Connection State</div>
              <div className="flex items-center">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  connectionStatus.is_connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {connectionStatus.state}
                </span>
              </div>
            </div>
            {connectionStatus.conversation_state && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Conversation State</div>
                <div className="text-blue-600 font-medium">{connectionStatus.conversation_state}</div>
              </div>
            )}
            {connectionStatus.last_connected && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Last Connected</div>
                <div className="text-gray-800 text-sm">{new Date(connectionStatus.last_connected).toLocaleString()}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <InfoIcon className="w-12 h-12 mx-auto mb-4" />
            <p>Connection status unavailable</p>
            <button 
              onClick={loadConnectionStatus}
              className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Connection Configuration Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="font-medium text-blue-800">About VoiceLive Connection Management</h4>
        <p className="text-sm text-blue-600 mt-2">
          These settings control the exponential backoff and retry behavior for Azure VoiceLive WebSocket connections. 
          Changes will apply to new connections after the configuration is refreshed.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h5 className="font-medium text-gray-700 mb-3">Timing Settings</h5>
          {renderFormField('voicelive_connection_initial_delay', 'Initial Delay (seconds)', 'number', 0.1, 10.0, 0.1)}
          {renderFormField('voicelive_connection_max_delay', 'Maximum Delay (seconds)', 'number', 1.0, 300.0, 1.0)}
          {renderFormField('voicelive_connection_backoff_multiplier', 'Backoff Multiplier', 'number', 1.1, 10.0, 0.1)}
          {renderFormField('voicelive_connection_health_check_interval', 'Health Check Interval (seconds)', 'number', 5, 300, 1)}
        </div>
        <div>
          <h5 className="font-medium text-gray-700 mb-3">Retry Settings</h5>
          {renderFormField('voicelive_connection_max_retries', 'Maximum Retries', 'number', 1, 50, 1)}
          {renderFormField('voicelive_connection_jitter_enabled', 'Enable Jitter', 'boolean')}
          {renderFormField('voicelive_transport_error_patterns', 'Transport Error Patterns (comma-separated)', 'text')}
        </div>
      </div>
    </div>
  );

  const renderVoiceLiveSessionTab = () => {
    // Filter and group VoiceLive session settings
    const sessionSettings = Object.entries(voiceLiveConfig || {})
      .filter(([key, setting]) => 
        key.startsWith('voicelive_vad_') || 
        key.startsWith('voicelive_voice_') || 
        key.startsWith('voicelive_audio_')
      )
      .sort(([a], [b]) => a.localeCompare(b));

    // Group settings by category
    const vadTypeSettings = sessionSettings.filter(([key]) => key.includes('_type'));
    const vadThresholdSettings = sessionSettings.filter(([key]) => key.includes('threshold'));
    const vadPaddingSettings = sessionSettings.filter(([key]) => key.includes('padding'));
    const vadSilenceSettings = sessionSettings.filter(([key]) => key.includes('silence'));
    const vadSemanticSettings = sessionSettings.filter(([key]) => key.includes('semantic'));
    const voiceSettings = sessionSettings.filter(([key]) => key.startsWith('voicelive_voice_'));
    const audioSettings = sessionSettings.filter(([key]) => key.startsWith('voicelive_audio_'));

    const renderSetting = (key: string, setting: any) => {
      const isBoolean = setting.data_type === 'boolean';
      const isNumber = setting.data_type === 'integer' || setting.data_type === 'float';
      
      // Define select options for VAD types and Voice configuration
      const vadTypeOptions = [
        { value: 'server_vad', label: 'Basic Server VAD' },
        { value: 'azure_semantic_vad', label: 'Azure Semantic VAD' },
        { value: 'azure_semantic_vad_en', label: 'Azure Semantic VAD (English)' },
        { value: 'azure_semantic_vad_multilingual', label: 'Azure Semantic VAD (Multilingual)' }
      ];

      const voiceOptions = [
        // Female Multilingual Voices (Recommended for Education)
        { value: 'en-US-AvaMultilingualNeural', label: 'Ava (Female, Multilingual) - Friendly, Clear' },
        { value: 'en-US-EmmaMultilingualNeural', label: 'Emma (Female, Multilingual) - Warm, Professional' },
        { value: 'en-US-AmandaMultilingualNeural', label: 'Amanda (Female, Multilingual) - Energetic' },
        { value: 'en-US-CoraMultilingualNeural', label: 'Cora (Female, Multilingual) - Calm, Soothing' },
        { value: 'en-US-EvelynMultilingualNeural', label: 'Evelyn (Female, Multilingual) - Mature, Wise' },
        { value: 'en-US-LolaMultilingualNeural', label: 'Lola (Female, Multilingual) - Young, Playful' },
        { value: 'en-US-NancyMultilingualNeural', label: 'Nancy (Female, Multilingual) - Energetic, Fun' },
        { value: 'en-US-PhoebeMultilingualNeural', label: 'Phoebe (Female, Multilingual) - Empathetic' },
        { value: 'en-US-SerenaMultilingualNeural', label: 'Serena (Female, Multilingual) - Friendly' },
        { value: 'en-US-JennyMultilingualNeural', label: 'Jenny (Female, 26 Languages) - Professional' },
        
        // Male Multilingual Voices
        { value: 'en-US-AndrewMultilingualNeural', label: 'Andrew (Male, Multilingual) - Mature, Professional' },
        { value: 'en-US-BrianMultilingualNeural', label: 'Brian (Male, Multilingual) - Friendly, Clear' },
        { value: 'en-US-AdamMultilingualNeural', label: 'Adam (Male, Multilingual) - Authoritative' },
        { value: 'en-US-BrandonMultilingualNeural', label: 'Brandon (Male, Multilingual) - Young, Energetic' },
        { value: 'en-US-ChristopherMultilingualNeural', label: 'Christopher (Male, Multilingual) - Professional' },
        { value: 'en-US-DavisMultilingualNeural', label: 'Davis (Male, Multilingual) - Empathetic, Warm' },
        { value: 'en-US-DerekMultilingualNeural', label: 'Derek (Male, Multilingual) - Excited' },
        { value: 'en-US-DustinMultilingualNeural', label: 'Dustin (Male, Multilingual) - Young' },
        { value: 'en-US-LewisMultilingualNeural', label: 'Lewis (Male, Multilingual) - Professional' },
        { value: 'en-US-SamuelMultilingualNeural', label: 'Samuel (Male, Multilingual) - Mature' },
        { value: 'en-US-SteffanMultilingualNeural', label: 'Steffan (Male, Multilingual) - Young' },
        { value: 'en-US-RyanMultilingualNeural', label: 'Ryan (Male, 26 Languages) - Professional' },
        
        // OpenAI-style HD Voices
        { value: 'en-US-NovaMultilingualNeural', label: 'Nova (Female, OpenAI) - HD Quality' },
        { value: 'en-US-ShimmerMultilingualNeural', label: 'Shimmer (Female, OpenAI) - HD Quality' },
        { value: 'en-US-AlloyMultilingualNeural', label: 'Alloy (Male, OpenAI) - HD Quality' },
        { value: 'en-US-EchoMultilingualNeural', label: 'Echo (Male, OpenAI) - HD Quality' },
        { value: 'en-US-OnyxMultilingualNeural', label: 'Onyx (Male, OpenAI) - HD Quality' },
        { value: 'en-US-FableMultilingualNeural', label: 'Fable (Neutral, OpenAI) - HD Quality' },
        
        // Standard US English Voices
        { value: 'en-US-AvaNeural', label: 'Ava (Female) - Standard' },
        { value: 'en-US-EmmaNeural', label: 'Emma (Female) - Standard' },
        { value: 'en-US-JennyNeural', label: 'Jenny (Female) - Professional' },
        { value: 'en-US-AriaNeural', label: 'Aria (Female) - Expressive, Newscast' },
        { value: 'en-US-SaraNeural', label: 'Sara (Female) - Friendly' },
        { value: 'en-US-AndrewNeural', label: 'Andrew (Male) - Standard' },
        { value: 'en-US-BrianNeural', label: 'Brian (Male) - Standard' },
        { value: 'en-US-DavisNeural', label: 'Davis (Male) - Conversational' },
        { value: 'en-US-GuyNeural', label: 'Guy (Male) - Newscast' },
        { value: 'en-US-JasonNeural', label: 'Jason (Male) - Energetic' },
        { value: 'en-US-TonyNeural', label: 'Tony (Male) - Friendly' }
      ];

      const audioFormatOptions = [
        { value: 'raw-16khz-16bit-mono-pcm', label: 'Raw PCM 16kHz Mono' },
        { value: 'raw-24khz-16bit-mono-pcm', label: 'Raw PCM 24kHz Mono (Recommended)' },
        { value: 'raw-48khz-16bit-mono-pcm', label: 'Raw PCM 48kHz Mono (High Quality)' },
        { value: 'audio-16khz-32kbitrate-mono-mp3', label: 'MP3 16kHz 32kbps' },
        { value: 'audio-24khz-48kbitrate-mono-mp3', label: 'MP3 24kHz 48kbps' },
        { value: 'audio-24khz-96kbitrate-mono-mp3', label: 'MP3 24kHz 96kbps' },
        { value: 'audio-24khz-160kbitrate-mono-mp3', label: 'MP3 24kHz 160kbps (Recommended)' },
        { value: 'audio-48khz-192kbitrate-mono-mp3', label: 'MP3 48kHz 192kbps (High Quality)' }
      ];

      const voiceTypeOptions = [
        { value: 'azure-standard', label: 'Azure Standard' },
        { value: 'azure-neural', label: 'Azure Neural (Recommended)' },
        { value: 'openai-hd', label: 'OpenAI HD Quality' }
      ];

      const transcriptionModelOptions = [
        { value: 'whisper-1', label: 'Whisper v1 (OpenAI)' },
        { value: 'azure-whisper', label: 'Azure Whisper (Recommended)' },
        { value: 'azure-speech-to-text', label: 'Azure Speech-to-Text' }
      ];

      const noiseReductionOptions = [
        { value: 'none', label: 'None' },
        { value: 'azure_deep_noise_suppression', label: 'Azure Deep Noise Suppression (Recommended)' },
        { value: 'server_noise_reduction', label: 'Server-Side Noise Reduction' },
        { value: 'client_noise_reduction', label: 'Client-Side Noise Reduction' }
      ];

      const echoCancellationOptions = [
        { value: 'none', label: 'None' },
        { value: 'server_echo_cancellation', label: 'Server-Side Echo Cancellation (Recommended)' },
        { value: 'client_echo_cancellation', label: 'Client-Side Echo Cancellation' },
        { value: 'azure_echo_cancellation', label: 'Azure Echo Cancellation' }
      ];

      const selectOptions: Record<string, Array<{value: string, label: string}>> = {
        // VAD Type configurations (Scenario-based)
        'voicelive_vad_announcement_type': vadTypeOptions,
        'voicelive_vad_reading_type': vadTypeOptions,
        'voicelive_vad_conversation_type': vadTypeOptions,
        'voicelive_vad_quiz_type': vadTypeOptions,
        'voicelive_vad_roleplay_type': vadTypeOptions,
        
        // Legacy mode-based keys (for backward compatibility)
        'voicelive_vad_default_type': vadTypeOptions,
        'voicelive_vad_normal_mode_type': vadTypeOptions,
        'voicelive_vad_reading_mode_type': vadTypeOptions,
        'voicelive_vad_announcement_mode_type': vadTypeOptions,
        
        // Voice configurations (Age-based voices)
        'voicelive_voice_default_6_8': voiceOptions,
        'voicelive_voice_default_9_11': voiceOptions,
        'voicelive_voice_default_12_14': voiceOptions,
        'voicelive_voice_default_15_16': voiceOptions,
        'voicelive_voice_default_17_18': voiceOptions,
        
        // Voice type configuration
        'voicelive_voice_type': voiceTypeOptions,
        
        // Audio format configurations
        'voicelive_audio_output_format': audioFormatOptions,
        'voicelive_audio_input_format': audioFormatOptions.filter(opt => opt.value.startsWith('raw-')),
        
        // Audio processing configurations
        'voicelive_audio_transcription_model': transcriptionModelOptions,
        'voicelive_audio_noise_reduction_type': noiseReductionOptions,
        'voicelive_audio_echo_cancellation_type': echoCancellationOptions
      };

      // Create human-readable labels with grade mappings for voice settings
      const getDisplayLabel = (key: string) => {
        const gradeMapping: Record<string, string> = {
          'voicelive_voice_default_6_8': 'Voice Default Grades K-3 (Ages 6-8)',
          'voicelive_voice_default_9_11': 'Voice Default Grades 4-5 (Ages 9-11)',
          'voicelive_voice_default_12_14': 'Voice Default Grades 6-9 (Ages 12-14)',
          'voicelive_voice_default_15_16': 'Voice Default Grades 9-12 (Ages 15-16)',
          'voicelive_voice_default_17_18': 'Voice Default Grades 11-12 (Ages 17-18)',
          'voicelive_voice_temperature_6_8': 'Voice Temperature Grades K-3 (Ages 6-8)',
          'voicelive_voice_temperature_9_11': 'Voice Temperature Grades 4-5 (Ages 9-11)',
          'voicelive_voice_temperature_12_14': 'Voice Temperature Grades 6-9 (Ages 12-14)',
          'voicelive_voice_temperature_15_16': 'Voice Temperature Grades 9-12 (Ages 15-16)',
          'voicelive_voice_temperature_17_18': 'Voice Temperature Grades 11-12 (Ages 17-18)',
        };
        
        return gradeMapping[key] || key.replace(/voicelive_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };

      return (
        <div key={key} className="bg-gray-50 p-3 rounded">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getDisplayLabel(key)}
          </label>
          <p className="text-xs text-gray-500 mb-2">{setting.description}</p>
          
          {selectOptions[key] ? (
            <select
              value={formData[key] ?? setting.value}
              onChange={(e) => handleInputChange(key, e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              {selectOptions[key].map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : isBoolean ? (
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData[key] ?? setting.value}
                onChange={(e) => handleInputChange(key, e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm">{formData[key] ?? setting.value ? 'Enabled' : 'Disabled'}</span>
            </label>
          ) : isNumber ? (
            <input
              type="number"
              value={formData[key] ?? setting.value}
              onChange={(e) => {
                const value = setting.data_type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value);
                handleInputChange(key, value);
              }}
              step={setting.data_type === 'float' ? '0.1' : '1'}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <input
              type="text"
              value={formData[key] ?? setting.value}
              onChange={(e) => handleInputChange(key, e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />
          )}
          
          <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
            <span>Type: {setting.data_type}</span>
            {setting.updated_at && <span>Updated: {new Date(setting.updated_at).toLocaleString()}</span>}
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="font-medium text-blue-800">About VoiceLive Session Configuration</h4>
          <p className="text-sm text-blue-600 mt-2">
            Configure Voice Activity Detection (VAD), voice synthesis, and audio processing settings for VoiceLive sessions. 
            Changes affect session behavior after configuration refresh.
          </p>
        </div>

        {sessionSettings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No VoiceLive session settings available yet.</p>
            <p className="text-sm mt-2">Please restart the backend to apply migrations.</p>
          </div>
        ) : (
          <>
            {/* VAD Type Selection */}
            {vadTypeSettings.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="mr-2">🎚️</span>
                  VAD Type Configuration
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {vadTypeSettings.map(([key, setting]) => renderSetting(key, setting))}
                </div>
              </div>
            )}

            {/* VAD Thresholds */}
            {vadThresholdSettings.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="mr-2">📊</span>
                  VAD Thresholds
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {vadThresholdSettings.map(([key, setting]) => renderSetting(key, setting))}
                </div>
              </div>
            )}

            {/* VAD Padding & Silence */}
            {(vadPaddingSettings.length > 0 || vadSilenceSettings.length > 0) && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="mr-2">⏱️</span>
                  VAD Timing Parameters
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm mb-2">Prefix Padding (ms)</h4>
                    {vadPaddingSettings.map(([key, setting]) => renderSetting(key, setting))}
                  </div>
                  <div>
                    <h4 className="font-medium text-sm mb-2">Silence Duration (ms)</h4>
                    {vadSilenceSettings.map(([key, setting]) => renderSetting(key, setting))}
                  </div>
                </div>
              </div>
            )}

            {/* Semantic VAD Settings */}
            {vadSemanticSettings.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="mr-2">🧠</span>
                  Semantic VAD Settings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {vadSemanticSettings.map(([key, setting]) => renderSetting(key, setting))}
                </div>
              </div>
            )}

            {/* Voice Settings */}
            {voiceSettings.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="mr-2">🗣️</span>
                  Voice Configuration
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {voiceSettings.map(([key, setting]) => renderSetting(key, setting))}
                </div>
              </div>
            )}

            {/* Audio Settings */}
            {audioSettings.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="mr-2">🔊</span>
                  Audio Configuration
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {audioSettings.map(([key, setting]) => renderSetting(key, setting))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">System Information</h3>
        {systemInfo ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong className="text-gray-600">Browser:</strong>
                <p className="text-gray-800 mt-1">{systemInfo.userAgent}</p>
              </div>
              <div>
                <strong className="text-gray-600">Language:</strong>
                <p className="text-gray-800 mt-1">{systemInfo.language}</p>
              </div>
              <div>
                <strong className="text-gray-600">Platform:</strong>
                <p className="text-gray-800 mt-1">{systemInfo.platform}</p>
              </div>
              <div>
                <strong className="text-gray-600">Timezone:</strong>
                <p className="text-gray-800 mt-1">{systemInfo.timezone}</p>
              </div>
              <div>
                <strong className="text-gray-600">Screen Resolution:</strong>
                <p className="text-gray-800 mt-1">{systemInfo.screen.width} x {systemInfo.screen.height}</p>
              </div>
              <div>
                <strong className="text-gray-600">Timestamp:</strong>
                <p className="text-gray-800 mt-1">{new Date(systemInfo.timestamp).toLocaleString()}</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">Loading system information...</p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Version Information</h3>
          <button
            onClick={() => setShowVersionDetails(!showVersionDetails)}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            {showVersionDetails ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        {showVersionDetails ? (
          <VersionDisplay variant="detailed" />
        ) : (
          <div className="bg-gray-50 rounded-lg p-4">
            <VersionDisplay variant="badge" className="mb-2" />
            <p className="text-xs text-gray-600">
              Click "Show Details" for complete version information including build metadata and release notes.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // const _renderLogsTab = () => null;
  /*
  const _renderLogsTab = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Debug Logs</h3>
          <div className="flex space-x-2">
            <button
              onClick={handleExportLogs}
              className="px-3 py-2 text-blue-600 hover:text-blue-700 text-sm border border-blue-300 rounded hover:bg-blue-50"
            >
              Export All
            </button>
            <button
              onClick={handleClearLogs}
              className="px-3 py-2 text-red-600 hover:text-red-700 text-sm border border-red-300 rounded hover:bg-red-50"
            >
              Clear Logs
            </button>
            <button
              onClick={loadRecentLogs}
              className="px-3 py-2 text-gray-600 hover:text-gray-700 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
          {logs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No logs available</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div key={index} className="text-xs bg-white p-3 rounded border-l-2 border-blue-200">
                  <div className="font-mono text-gray-500 mb-1">
                    {log.timestamp} - <span className="font-semibold">{log.level}</span> - {log.category}
                  </div>
                  <div className="text-gray-700">{log.event}</div>
                  {log.data && (
                    <pre className="text-gray-600 mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  */

  const renderHelpTab = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Troubleshooting Guide</h3>
        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-medium text-gray-800">🎤 Microphone Issues</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Check browser permissions for microphone access</li>
              <li>• Ensure microphone is not being used by other applications</li>
              <li>• Try refreshing the page and granting permissions again</li>
              <li>• Check system microphone settings and volume levels</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="font-medium text-gray-800">🔌 Connection Problems</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Verify internet connectivity</li>
              <li>• Check if Azure VoiceLive service is accessible</li>
              <li>• Review VoiceLive connection settings in this panel</li>
              <li>• Monitor connection logs for error patterns</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-yellow-500 pl-4">
            <h4 className="font-medium text-gray-800">🔊 Audio Issues</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Check speaker/headphone connections</li>
              <li>• Verify browser audio settings</li>
              <li>• Try using a different browser or device</li>
              <li>• Clear browser cache and cookies</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-red-500 pl-4">
            <h4 className="font-medium text-gray-800">🚨 Persistent Issues</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Export system logs and share with technical support</li>
              <li>• Document specific error messages and reproduction steps</li>
              <li>• Try using the application in incognito/private browsing mode</li>
              <li>• Contact administrator with system information from this panel</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-medium text-blue-800">📞 Technical Support</h4>
        <p className="text-sm text-blue-600 mt-2">
          For additional support, export your system logs and configuration settings using the buttons in this panel. 
          This information will help technical support diagnose and resolve issues more quickly.
        </p>
      </div>

      {/* Developer Tools Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h4 className="font-medium text-yellow-800">🔧 Developer Tools</h4>
        <p className="text-sm text-yellow-600 mt-2 mb-4">
          Tools for development, testing, and demo purposes. Use with caution in production environments.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={handleSeedMockData}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Seeding Data...
              </div>
            ) : (
              '📊 Seed Mock Data'
            )}
          </button>
          
          <button
            onClick={handleClearData}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Processing...' : '🗑️ Clear All Data'}
          </button>
        </div>
        
        <div className="mt-3 text-xs text-yellow-600">
          <p><strong>Seed Mock Data:</strong> Adds sample stories, sessions, and analytics data for demo purposes</p>
          <p><strong>Clear All Data:</strong> Removes all dashboard data (cannot be undone)</p>
        </div>
      </div>
    </div>
  );

  const renderFormField = (key: string, label: string, type: 'number' | 'boolean' | 'text', min?: number, max?: number, step?: number) => {
    const value = formData[key];
    const config = voiceLiveConfig?.[key];

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
        
        {type === 'boolean' ? (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleInputChange(key, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-600">Enable</span>
          </div>
        ) : (
          <input
            type={type}
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => handleInputChange(key, type === 'number' ? Number(e.target.value) : e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        )}
        
        {config?.description && (
          <p className="mt-1 text-xs text-gray-500">{config.description}</p>
        )}
        
        {type === 'number' && (min !== undefined || max !== undefined) && (
          <p className="mt-1 text-xs text-gray-400">
            Range: {min ?? 'no minimum'} - {max ?? 'no maximum'}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3">Loading configuration...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-6xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Enhanced System Configuration & Diagnostics</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'voicelive', label: 'VoiceLive Connection', icon: '🔌' },
              { key: 'voicelive_session', label: 'VoiceLive Session', icon: '�️' },
              { key: 'prompts', label: 'Persona Prompts', icon: '🎨' },
              { key: 'system', label: 'System Info', icon: '💻' },
              { key: 'logs', label: 'Backend Logs', icon: '📝' },
              { key: 'database', label: 'Database', icon: '🗄️' },
              { key: 'help', label: 'Help', icon: '❓' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Global Error and Success Messages */}
          {errors.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertIcon className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Configuration Errors</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc pl-5">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'voicelive' && renderVoiceLiveTab()}
          {activeTab === 'voicelive_session' && renderVoiceLiveSessionTab()}
          {activeTab === 'prompts' && renderPromptsTab()}
          {activeTab === 'system' && renderSystemTab()}
          {activeTab === 'logs' && <BackendLogsViewer />}
          {activeTab === 'database' && <DatabaseStatusViewer />}
          {activeTab === 'help' && renderHelpTab()}


        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {activeTab === 'voicelive' && 'Configure VoiceLive connection settings and monitor status'}
            {activeTab === 'voicelive_session' && 'Configure VAD, voice, and audio processing settings'}
            {activeTab === 'prompts' && 'Configure AI persona prompts (single prompt per persona, applies to all grades)'}
            {activeTab === 'system' && 'View system and browser information'}
            {activeTab === 'logs' && 'View backend application logs with filtering and download'}
            {activeTab === 'database' && 'Monitor database status, tables, and migrations'}
            {activeTab === 'help' && 'Troubleshooting guide and support'}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
            {(activeTab === 'voicelive' || activeTab === 'voicelive_session') && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Configuration'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}